import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import PageSEO from '@/components/PageSEO';
import axios from 'axios';
import { Eye, EyeOff, ArrowRight, Loader2, Check, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState(token ? 'form' : 'error');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleReset = useCallback(async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(''); setBusy(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setStatus('success');
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed. Link may have expired.');
    }
    setBusy(false);
  }, [password, confirm, token]);

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
      <PageSEO title="Reset Password" description="Set a new password for your OmniView account." />
      <div className={`w-full max-w-md rounded-2xl p-8 ${isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-zinc-200 shadow-xl'}`}>
        {status === 'form' && (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold">Set New Password</h1>
              <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Choose a strong password for your account</p>
            </div>
            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">{error}</div>}
            <form onSubmit={handleReset} className="space-y-4">
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="New password (min 6 chars)" required
                  className={`w-full px-4 py-3 pr-10 rounded-lg text-sm border outline-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} />
                <button type="button" onClick={() => setShowPw(!showPw)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Confirm password" required
                className={`w-full px-4 py-3 rounded-lg text-sm border outline-none ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`} />
              <button type="submit" disabled={busy}
                className="w-full py-3 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                {busy ? <Loader2 size={16} className="animate-spin"/> : null} Reset Password <ArrowRight size={16}/>
              </button>
            </form>
          </>
        )}
        {status === 'success' && (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#06D6A0' }}>
              <Check size={24} className="text-white"/>
            </div>
            <h2 className="text-xl font-bold mb-2">Password Reset!</h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Your password has been updated.</p>
            <button onClick={() => navigate('/auth')} className="px-6 py-2.5 rounded-lg text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>Sign In</button>
          </div>
        )}
        {status === 'error' && (
          <div className="text-center py-4">
            <AlertTriangle size={36} className="mx-auto mb-4 text-orange-500"/>
            <h2 className="text-xl font-bold mb-2">Invalid Link</h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>This reset link is invalid or has expired.</p>
            <button onClick={() => navigate('/auth')} className={`px-6 py-2.5 rounded-lg text-sm font-medium border ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-zinc-300 text-zinc-700'}`}>
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
